<template>
  <Modal v-if="display" @hideModal="toggleModal" :wide=true id="carousel-modal">
    <h1 class="font-bold text-lg text-left mt-0">Added to Cart</h1>
    <div class="grid grid-cols-2 md:grid-cols-3 gap-4">
      <div class="col-span-2 md:col-span-2 text-left">
        <img
          align="left"
          style="max-height: 100px"
          class="mr-5"
          v-if="service.ServiceImage"
          :src="service.ServiceImage"
        />
        <h3 v-text="service.ServiceName"></h3>
        <span v-if="service.isQuoteOnly"> Quote request </span>
        <span v-else class="small">
          {{ formatPrice(service.mainPrice) }}
          <span v-if="service.isMonthly"> per month </span>
          <span v-else> per visit </span>
        </span>
      </div>
      <div class="md:text-right col-span-2 md:col-span-1">
        <a href="#" class="button mb-5" @click.stop.prevent="toggleModal"
          >Continue Shopping</a
        >
        <NuxtLink to="/cart/" class="button">Go to Cart</NuxtLink>
      </div>
    </div>

    <!-- <div
      v-if="addonServices && addonServices.length > 0"
      class="pt-5 max-h-80 overflow-y-auto"
    >
      <hr />
      <div class="grid grid-cols-1 md:grid-cols-3">
        <AddedToCartModalServiceCard
          v-for="addonService in addonServices"
          :key="addonService.ServiceDefId"
          :service="addonService"
          :serviceData="serviceData"
        />
      </div>
    </div> -->

    <div class="mt-5 border-top">
      <div class="pt-2 pb-5 font-bold title">Add Popular Service To Your Order</div>
      <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div class="service-item" v-for="(item, key) in serviceRecord" :key="key">
          <div class="service-image" :style="{ backgroundImage: 'url(' + item.ServiceImage + ')' }">
          </div>
          <div class="flex pt-2 pb-2 justify-between items-baseline">
            <div class="service-name pt-4">
              <div> {{ item.ServiceName }} </div>
              <NuxtLink :to="'/services/' + item.ServiceSlug + '/'">
                <span style="font-size: 0.75rem;font-weight:bold">Learn more</span>
              </NuxtLink>
            </div>
            <span style="width: 35%;text-align: center;">
              <h3 class="txt-00953A m-0">${{ item.mainPrice }}</h3>
              <!-- <span class="price-per-month">Per Month</span> -->
              <span v-if="item.isMonthly" class="price-per-month">
                Per Month
              </span>
              <span class="price-per-month" v-else>
                Per visit
              </span>
            </span>
          </div>
          <p class="pt-3">
            <a
              href="#" class="button visible" :class="{
                'added-to-cart button-added': addedToCart.length > 0 && addedToCart.indexOf(item.ServiceDefId) > -1,
                'add-to-cart': !(addedToCart.length > 0 && addedToCart.indexOf(item.ServiceDefId) > -1)
              }"
              @click.stop.prevent="addToCart(item, key)"
            >
              {{ (addedToCart.length > 0 && addedToCart.indexOf(item.ServiceDefId) > -1) ? "Added to Cart" : "Add to Cart" }}
            </a>
          </p>
        </div>
      </div>
    </div>
		<Toast v-if="showToast" @hideToast="toggleToast">{{ message }}</Toast>
  </Modal>
</template>

<script setup lang="ts">
import { ref, computed } from "vue";
import { useStore } from "vuex";
import type { Store } from "vuex/types/index.js";
import type State from "~/domains/store/State";
import { useNuxtApp } from "#app";
import { getFormattedPrice } from "~/utils/payment";
import type ServiceState from "~/domains/store/services/ServiceState";

const props = defineProps({
  service: {
    type: Object,
    required: true,
  },
  serviceData: {
    type: Object,
    required: true,
  },
  addedToCart: {
    type: Array,
    default: () => [],
  },
});

const store: Store<State> = useStore();
const { $services } = useNuxtApp();

const display = ref(false);
const addonServices = ref<ServiceState[]>([]);
const addedToCart = ref<number[]>([]);
const serviceRecord = ref<ServiceState[]>([]);
const message = ref('There was a problem. Please try again at another time.');
const showToast = ref(false);

const useStoreServiceData = computed(() => {
  return !!store.state.persistedState.serviceData?.services;
});

const show = async () => {
  const addonServiceIds = props.service.addons;
  if (addonServiceIds) {
    let addonServicesList: ServiceState[] = [];
    if (useStoreServiceData.value) {
      addonServicesList = $services.getServicesByIds(addonServiceIds, false, false);
    } else {
      addonServicesList = props.serviceData.getServicesByIds(addonServiceIds);
    }

    addonServices.value = addonServicesList.filter(
      (service) => !service.addedToCart && service.showPurchase
    );
  }

  display.value = true;

  setTimeout(() => {
    const elementToMove = document.getElementById("carousel-modal");
    const targetElement = document.getElementById("isCarousel-modal");
    if (elementToMove && targetElement) {
      targetElement.parentNode?.insertBefore(
        elementToMove,
        targetElement.nextSibling
      );
    }
  });
};

const hide = () => {
  display.value = false;
};

const toggleModal = () => {
  display.value = !display.value;
};

const toggleToast = () => {
  showToast.value = !showToast.value;
};

defineExpose({show, hide, toggleModal});

const formatPrice = (value: number) => {
  return getFormattedPrice(value);
};

const loadServiceData = async () => {
  const persistedState = store.state.persistedState;
  if (!persistedState) return;

  const { franchiseData } = persistedState;
  const allServiceIds = franchiseData?.services_to_display ?? [];
  const addonUpsellServiceIds = allServiceIds.filter((serviceId) => !["2", "3"].includes(serviceId));
  const addonUpsellServices: ServiceState[] = $services.getServicesByIds(addonUpsellServiceIds, true, false);

  const orderItems = persistedState?.order?.orderItems;

  const data = addonUpsellServices.filter(obj => !orderItems.some(item =>
    item.uP_ServiceDefinitionId === obj.ServiceDefId
  ));

  serviceRecord.value = data?.slice(0, 3);
};

const addToCart = async (service: ServiceState, key: number) => {
  const response = await $services.addServiceToCart(service)

  if (response.successful) {
    addedToCart.value.push(service.ServiceDefId);
  } else {
    message.value = response?.message || 'There was a problem. Please try again at another time.';
    showToast.value = true;
  }
};

onMounted(() => {
  loadServiceData();
});

</script>

<style scoped>
.service-image {

background-size: cover;
background-repeat: no-repeat;
background-position: bottom center;
height: 175px;
border-radius: 1rem;
min-width: 250px;
margin: 0 auto;
}


.service-name {
    font-size: 1rem;
    font-weight: 600;
}

.txt-00953A {
    color: #00953A;
}

.add-to-cart {
    display: inline-block;
    max-width: 8rem;
    font-size: 0.75rem;
    padding: 5px;
    text-align: center;
    background-color: #00953A;
    color: white;
    border-radius: 0.5rem;
    text-decoration: none;
}

.added-to-cart {
    display: inline-block;
    max-width: 8rem;
    font-size: 0.75rem;
    padding: 5px;
    text-align: center;
    background-color: #fff;
    color: #00953A;
    border-radius: 0.5rem;
    text-decoration: none;
    border: 1px solid #00953A;
}

.border-top {
    border-top: 1px solid #517195;
}
</style>
